import { dyOf, type AimState } from "../ui/aim-gesture.js";

/**
 * DCTA-216 (docs/design/v3/10-welcome.md §0.6) — step 1's lesson on the real mark, as a pure function of the time since
 * the step opened (ms): the mark's own view (`state`, `p`) and the ghost fingertip (`gy` px up, opacity `go`, scale `gs`).
 * One full swipe (press, slide, the mic reached, back to rest), then a short second slide that stops three quarters up:
 * the still picture, reached at 3.53 s (inside WCAG 2.2.2's five seconds), never a loop. Reduced motion shows the still
 * picture from the first frame. With the microphone off the lesson only taps. The mock-up's own functions
 * (`mockups/24-welcome.html`), typed.
 */
export interface LessonFrame {
  readonly state: AimState;
  readonly p: number;
  readonly gy: number;
  readonly go: number;
  readonly gs: number;
}

export const STILL: LessonFrame = { state: "dragging", p: 0.75, gy: 72, go: 1, gs: 0.92 };
export const STILL_TAP: LessonFrame = { state: "pressed", p: 0, gy: 0, go: 1, gs: 0.92 };
/** When each lesson rests on its still picture (ms). */
export const END = { swipe: 3530, tap: 1680 } as const;

const clamp = (x: number, a: number, b: number): number => Math.max(a, Math.min(b, x));
const seg = (t: number, a: number, b: number): number => clamp((t - a) / (b - a), 0, 1);
const inOut = (x: number): number => (x < 0.5 ? 2 * x * x : 1 - (-2 * x + 2) ** 2 / 2);
const easeOut = (x: number): number => 1 - (1 - x) ** 3;
const easeIn = (x: number): number => x * x * x;

const REST: LessonFrame = { state: "idle", p: 0, gy: 0, go: 0, gs: 1.15 };
const PRESSED: LessonFrame = { state: "pressed", p: 0, gy: 0, go: 1, gs: 0.92 };
/** The ghost arriving at `a`: fades in and settles from 1.15 to 1 over 160 ms. */
const ghostIn = (t: number, a: number): LessonFrame => {
  const k = easeOut(seg(t, a, a + 160));
  return { state: "idle", p: 0, gy: 0, go: k, gs: 1.15 - 0.15 * k };
};
/** The ghost lifting away from `a` (still `gy` up): fades out and grows back over 160 ms. */
const ghostOut = (t: number, a: number, gy: number): LessonFrame => {
  const k = easeIn(seg(t, a, a + 160));
  return { state: "idle", p: 0, gy, go: 1 - k, gs: 0.92 + 0.13 * k };
};

export function swipeAt(t: number): LessonFrame {
  if (t < 400) return REST;
  if (t < 560) return ghostIn(t, 400);
  if (t < 760) return PRESSED;
  if (t < 1520) {
    const p = inOut(seg(t, 760, 1520));
    return { state: "dragging", p, gy: 96 * p, go: 1, gs: 0.92 };
  }
  if (t < 2080) return { state: "drag-commit", p: 1, gy: 96, go: 1, gs: 0.92 };
  if (t < 2240) return ghostOut(t, 2080, 96);
  if (t < 2640) return REST;
  if (t < 2800) return ghostIn(t, 2640);
  if (t < 2960) return PRESSED;
  if (t < END.swipe) {
    const p = 0.75 * easeOut(seg(t, 2960, END.swipe));
    return { state: "dragging", p, gy: 96 * p, go: 1, gs: 0.92 };
  }
  return STILL;
}

export function tapAt(t: number): LessonFrame {
  if (t < 400) return REST;
  if (t < 560) return ghostIn(t, 400);
  if (t < 760) return PRESSED;
  if (t < 920) return ghostOut(t, 760, 0);
  if (t < 1520) return REST;
  if (t < END.tap) return ghostIn(t, 1520);
  return STILL_TAP;
}

/** The drop's lift in a frame: the gesture's own mapping while the mark slides, at rest otherwise. */
export function dropDy(f: LessonFrame): number {
  return f.state === "dragging" || f.state === "drag-commit" ? dyOf(f.gy) : 0;
}
